import { styled } from '@linaria/react';
import { type ReactNode, useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isTextOverflowingClamp, resolveLineHeightPx } from './agent-collapsible-user-text.helpers';
import { useAgentMessageListScroll } from './agent-message-list-scroll.context';
import { USER_MESSAGE_CLAMP_LINES } from './chat.constants';

interface AgentCollapsibleUserTextProps {
  children: ReactNode;
}

/**
 * Clamps a sent user message to a fixed number of lines with a "Show more" /
 * "Show less" toggle, so a long paste doesn't take over the chat.
 *
 * Responsibilities:
 * 1. Measure the text against the clamp on mount and whenever its box
 *    resizes (e.g. the side panel is resized), showing the toggle only when
 *    the clamp actually hides text
 * 2. Keep the expanded state local to this message
 * 3. Release the message list's stick-to-bottom follow on expand (unless a
 *    turn is live), so the revealed text grows in place instead of the view
 *    jumping away
 * 4. On collapse, scroll the list back to the message if it ended up above view
 *
 * The full text stays in the DOM while collapsed, so screen readers, copy and
 * find-in-page still see all of it.
 *
 * @param {ReactNode} children - The message's rendered text (plain spans, mentions, slash-command tags)
 * @returns {JSX.Element} The clamped text and, when it overflows, its toggle
 */
const AgentCollapsibleUserText = ({ children }: AgentCollapsibleUserTextProps) => {
  const { t } = useTranslation();
  const listScroll = useAgentMessageListScroll();
  const contentId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const measure = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const lineHeight = resolveLineHeightPx(window.getComputedStyle(content));
    setIsOverflowing(isTextOverflowingClamp(content.scrollHeight, lineHeight, USER_MESSAGE_CLAMP_LINES));
  }, []);

  // Layout effect so an overflowing message paints clamped on the first frame
  // instead of flashing at full height.
  useLayoutEffect(() => {
    measure();
    let isActive = true;
    // A collapsed box keeps its height when a web font swaps in, so the
    // ResizeObserver alone would miss the text reflowing to more lines.
    document.fonts?.ready?.then(() => {
      if (isActive) measure();
    });
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') {
      return () => {
        isActive = false;
      };
    }
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => {
      isActive = false;
      observer.disconnect();
    };
  }, [measure]);

  // After "Show less", bring the message back into view if collapsing left it
  // above the list viewport — browsers without scroll anchoring (Safari)
  // otherwise leave the reader on a later turn. Runs after the collapsed layout
  // commits so the measured position is the final one.
  const wasCollapsedByUserRef = useRef(false);
  useLayoutEffect(() => {
    if (isExpanded || !wasCollapsedByUserRef.current) return;
    wasCollapsedByUserRef.current = false;
    if (contentRef.current) listScroll?.revealElementTop(contentRef.current);
  }, [isExpanded, listScroll]);

  const handleToggle = () => {
    if (isExpanded) {
      wasCollapsedByUserRef.current = true;
    } else {
      listScroll?.releaseStickToBottomIfIdle();
    }
    setIsExpanded((previous) => !previous);
  };

  const isCollapsed = isOverflowing && !isExpanded;

  return (
    <>
      <ClampedContent id={contentId} ref={contentRef} data-collapsed={isCollapsed}>
        {children}
      </ClampedContent>
      {isOverflowing && (
        <ToggleButton type="button" aria-expanded={isExpanded} aria-controls={contentId} onClick={handleToggle}>
          {isExpanded ? t('flora.chat.user_message.show_less') : t('flora.chat.user_message.show_more')}
        </ToggleButton>
      )}
    </>
  );
};

const ClampedContent = styled.div`
  position: relative;

  &[data-collapsed='true'] {
    /* em fallback for browsers without the lh unit; the bubble's line-height is 1.5. */
    max-height: calc(1.5em * ${USER_MESSAGE_CLAMP_LINES});
    max-height: calc(1lh * ${USER_MESSAGE_CLAMP_LINES});
    /* clip, not hidden, so find-in-page and drag-select can't scroll the clipped text into the box. */
    overflow: hidden;
    overflow: clip;
  }

  /* Fades the last line into the bubble so the cut-off reads as intentional. */
  &[data-collapsed='true']::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2.25em;
    height: 1.5lh;
    background: linear-gradient(to bottom, transparent, var(--sky-lighter));
    pointer-events: none;
  }
`;

const ToggleButton = styled.button`
  display: block;
  margin: 4px 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  color: var(--flora-link-color);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid var(--flora-link-color);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export { AgentCollapsibleUserText };
